import type { GenericHighlight } from "@/types/Highlights";
import type {
  PremadeTranslationRow,
  CustomTranslationRow,
  TranslationRowDiff,
} from "@/features/experience-shared/highlights-v2/translation/types/highlight-translation";
import {
  getAddedItems,
  getRemovedItems,
  getDiffItems,
  mergeUnique,
} from "@/features/experience-highlights/utils/highlights-diff-utils";

/**
 * Pure mappers for the TRANSLATION view: zip the source (English) and target (translated) lists
 * into the editor's two-column rows, and project edited custom rows back onto the target.
 * Both premade and custom pair source<->target by `code`; the source drives the rows and their
 * order, and an unmatched source renders with an empty target rather than shifting positions.
 */

function byVisualizationOrder(a: GenericHighlight, b: GenericHighlight): number {
  return (a.visualization_order ?? 0) - (b.visualization_order ?? 0);
}

/**
 * Canonical shape for change detection: keeps only identity + translated `name`, dropping the
 * `action` tag, ordering and backend-only fields so an edited model and a reloaded list compare
 * equal. Used as the `useComplexModelModelWithChanges` normalizer.
 */
export function normalizeTranslationCustomHighlights(items: GenericHighlight[]): GenericHighlight[] {
  return [...items]
    .sort(byVisualizationOrder)
    .map((item) => ({ id: item.id, code: item.code, name: item.name } as GenericHighlight));
}

/** Read-only premade rows paired by `code`; unmatched source rows get an empty target. */
export function toPremadeRows(source: GenericHighlight[], target: GenericHighlight[]): PremadeTranslationRow[] {
  const targetByCode = new Map<string, GenericHighlight>();
  for (const item of target) {
    if (item.code != null) {
      targetByCode.set(item.code, item);
    }
  }

  return [...source]
    .sort(byVisualizationOrder)
    .filter((item): item is GenericHighlight & { code: string } => item.code != null)
    .map((item) => ({
      code: item.code,
      source: item.name,
      target: targetByCode.get(item.code)?.name ?? "",
    }));
}

/** Rows driven by the source, paired to the target by `code`; unmatched sources stay empty and non-editable. */
export function toCustomRows(source: GenericHighlight[], target: GenericHighlight[]): CustomTranslationRow[] {
  const targetByCode = new Map<string, GenericHighlight>();
  for (const item of target) {
    if (item.code != null) {
      targetByCode.set(item.code, item);
    }
  }

  return [...source]
    .sort(byVisualizationOrder)
    .filter((item): item is GenericHighlight & { code: string } => item.code != null)
    .map((sourceItem) => {
      const match = targetByCode.get(sourceItem.code);
      return {
        id: match?.id ?? sourceItem.code,
        code: sourceItem.code,
        source: sourceItem.name,
        translation: match?.name ?? "",
        editable: match != null,
      };
    });
}

/** Applies edits onto existing target records (by `id`); non-editable rows are ignored, never created. */
export function applyCustomRows(rows: CustomTranslationRow[], target: GenericHighlight[]): GenericHighlight[] {
  const rowsById = new Map(rows.filter((row) => row.editable).map((row) => [row.id, row]));

  return target.map((existing) => {
    const row = rowsById.get(existing.id);
    if (row == null || row.translation === existing.name) {
      return existing;
    }

    return {
      ...existing,
      name: row.translation,
      action: "EDIT",
    };
  });
}

// --- Diff mode (revision view) ------------------------------------------------------------
// The source column shows what changed between two snapshots (diffOld -> diffNew). Premade and
// custom are both diffed by `code` (the persisted uuid, stable across snapshots).

/** Resolves a row's diff marker from the add/remove membership sets (premade: no "modified"). */
function resolveDiff(
  key: string,
  addedKeys: Set<string>,
  removedKeys: Set<string>,
  previousSource?: string
): TranslationRowDiff | undefined {
  if (addedKeys.has(key)) {
    return { status: "added" };
  }
  if (removedKeys.has(key)) {
    return { status: "removed" };
  }
  if (previousSource != null) {
    return { status: "modified", previousSource };
  }
  return undefined;
}

/** Premade diff rows: merged source (old + new, by `code`) tagged `added`/`removed`. */
export function toPremadeRowsDiff(
  diffOld: GenericHighlight[],
  diffNew: GenericHighlight[],
  target: GenericHighlight[]
): PremadeTranslationRow[] {
  const added = new Set(
    getAddedItems(diffOld, diffNew, "code")
      .map((item) => item.code)
      .filter((code): code is string => code != null)
  );
  const removed = new Set(
    getRemovedItems(diffOld, diffNew, "code")
      .map((item) => item.code)
      .filter((code): code is string => code != null)
  );
  const merged = mergeUnique(diffOld, diffNew, "code");

  const targetByCode = new Map<string, GenericHighlight>();
  for (const item of target) {
    if (item.code != null) {
      targetByCode.set(item.code, item);
    }
  }

  return merged
    .filter((item): item is GenericHighlight & { code: string } => item.code != null)
    .map((item) => ({
      code: item.code,
      source: item.name,
      target: targetByCode.get(item.code)?.name ?? "",
      diff: resolveDiff(item.code, added, removed),
    }));
}

/** Custom diff rows for existing targets, tagged `added`/`removed`/`modified`, matched by `code`. */
export function toCustomRowsDiff(
  diffOld: GenericHighlight[],
  diffNew: GenericHighlight[],
  target: GenericHighlight[]
): CustomTranslationRow[] {
  const { added, removed, modified, merged } = getDiffItems(diffOld, diffNew, "code");
  const addedCodes = new Set(added.map((item) => item.code).filter((code): code is string => code != null));
  const removedCodes = new Set(removed.map((item) => item.code).filter((code): code is string => code != null));
  // getDiffItems was called with key "code", so ModifiedItem.id holds the code value.
  const modifiedByCode = new Map(modified.map((item) => [item.id, item]));

  return toCustomRows(merged, target).map((row) => ({
    ...row,
    diff: resolveDiff(row.code, addedCodes, removedCodes, modifiedByCode.get(row.code)?.oldValue),
  }));
}
